// Vertex handle — draggable point on a polygon vertex
// Part of the vertex edit mode (T04a)
// Drag to reposition a single vertex; the polygon updates in real-time

import * as THREE from 'three'
import type { Handle, EditableShape } from '@/types/editor'
import { DPM } from '@/constants/coordinates'

export class VertexHandle implements Handle {
  readonly type = 'vertex' as const
  readonly cursor = 'move'
  readonly mesh: THREE.Mesh
  readonly index: number

  private shape: EditableShape
  private dragStartWorld: THREE.Vector3 | null = null
  private startPoints: Array<{ x: number; y: number }> | null = null
  private rotCos: number = 1
  private rotSin: number = 0

  constructor(index: number, mesh: THREE.Mesh, shape: EditableShape) {
    this.index = index
    this.mesh = mesh
    this.shape = shape
  }

  onDragStart(worldPoint: THREE.Vector3): void {
    this.dragStartWorld = worldPoint.clone()
    this.startPoints = this.shape.getPoints()?.map(p => ({ ...p })) ?? null

    // Cache rotation for inverse transform (world → local frame)
    const rad = -this.shape.getRotation() * Math.PI / 180
    this.rotCos = Math.cos(rad)
    this.rotSin = Math.sin(rad)
  }

  onDrag(worldPoint: THREE.Vector3, _delta: THREE.Vector3): void {
    if (!this.dragStartWorld || !this.startPoints) return

    // World delta in editor units (dots)
    const worldDx = (worldPoint.x - this.dragStartWorld.x) * DPM
    const worldDz = (worldPoint.z - this.dragStartWorld.z) * DPM

    // Inverse Y-rotation: world → shape's local frame
    const cos = this.rotCos
    const sin = this.rotSin
    const localDx = worldDx * cos - worldDz * sin
    const localDz = worldDx * sin + worldDz * cos

    // Update just the dragged vertex (round to grid snap)
    const newPoints = this.startPoints.map((p, i) => {
      if (i === this.index) {
        return { x: Math.round(p.x + localDx), y: Math.round(p.y + localDz) }
      }
      return { ...p }
    })

    this.shape.setPoints(newPoints)

    // Update bounding box to match new point positions
    const xs = newPoints.map(p => p.x)
    const ys = newPoints.map(p => p.y)
    const width = Math.max(...xs) - Math.min(...xs)
    const height = Math.max(...ys) - Math.min(...ys)
    this.shape.resize(Math.max(1, width), Math.max(1, height))
  }

  onDragEnd(): void {
    this.dragStartWorld = null
    this.startPoints = null
    this.shape.commit()
  }
}
